import React, { useState, useCallback, useEffect } from 'react';
import { parseOpenJobs, parseARaging, parseCashflow, computeClientMetrics, computeWeeklyJobStats, computeGlobalFlags } from './parsers';
import { parseJobsHistory, computeWeeklyVolumeStats, computeClientVolumeSparklines, computeHistoryAlerts, computeGhostJobs, computeRevenueGap } from './parsers/jobsHistory';
import { buildUnifiedClients, computeUnifiedFlags } from './parsers/unifiedClient';
import { loadOpsData, saveOpsData, loadJobsHistory, saveJobsHistory, loadClientMasterDB, saveClientMasterDB, emptyEntityData, getSession } from './supabase';
import { updateClient } from './data/clientMaster';
import ClientPulse from './views/ClientPulse';
import JobsPipeline from './views/JobsPipeline';
import Collections from './views/Collections';
import CashView from './views/CashView';
import FlagsView from './views/FlagsView';
import MonthlyMIS from './views/MonthlyMIS';
import SaudiOps from './views/SaudiOps';
import FileUpload from './components/FileUpload';
import ClientSetup from './components/ClientSetup';
import Login, { checkAuth } from './components/Login';
import { signOut } from './supabase';
import './App.css';

const ADMIN_EMAIL = 'email@suchitsehgal.com';

const ENTITIES = [
  { id: 'uae', label: 'UAE 🇦🇪', flag: '🇦🇪' },
  { id: 'saudi', label: 'Saudi 🇸🇦', flag: '🇸🇦' },
  { id: 'group', label: 'Group 🌐', flag: '🌐' },
];

const TABS = [
  { id: 'flags', label: 'Flags' },
  { id: 'pulse', label: 'Client Pulse' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'opshistory', label: 'Ops History' },
  { id: 'collections', label: 'Collections' },
  { id: 'cash', label: 'Cash' },
  { id: 'monthly', label: 'Monthly MIS' },
];

function getEmailFromSession() {
  try {
    const session = getSession();
    if (!session?.access_token) return null;
    const payload = JSON.parse(atob(session.access_token.split('.')[1]));
    return payload.email || null;
  } catch {
    return null;
  }
}

export default function App() {
  const [authed, setAuthed] = useState(checkAuth());
  const [accessDenied, setAccessDenied] = useState(false);

  const handleLogout = async () => {
    await signOut();
    setAuthed(false);
    setAccessDenied(false);
  };

  const [entity, setEntity] = useState('uae');
  const [tab, setTab] = useState('flags');
  const [showUpload, setShowUpload] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [clientMaster, setClientMaster] = useState({});
  const [data, setData] = useState({ uae: emptyEntityData(), saudi: emptyEntityData() });
  const [jobsHistory, setJobsHistory] = useState({ uae: [], saudi: [] });
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    if (!authed) return;
    const email = getEmailFromSession();
    if (!email || email.toLowerCase() !== ADMIN_EMAIL) {
      signOut();
      setAuthed(false);
      setAccessDenied(true);
      return;
    }
    setLoading(true);
    Promise.all([
      loadOpsData(),
      loadClientMasterDB(),
      loadJobsHistory('uae'),
      loadJobsHistory('saudi'),
    ]).then(([opsData, clientMasterData, uaeHistory, saudiHistory]) => {
      setData(opsData);
      setClientMaster(clientMasterData);
      setJobsHistory({ uae: uaeHistory || [], saudi: saudiHistory || [] });
      setLoading(false);
    }).catch(err => {
      console.error('Failed to load data:', err);
      setLoading(false);
    });
  }, [authed]);

  const handleFiles = useCallback((entityId, files) => {
    const opsUpdates = {};
    let historyJobs = null;

    const promises = files.map(({ type, buffer }) => {
      return new Promise((resolve) => {
        try {
          if (type === 'jobs') {
            opsUpdates.jobs = parseOpenJobs(buffer);
            opsUpdates.loaded = { ...(opsUpdates.loaded || {}), jobs: true };
          } else if (type === 'jobsHistory') {
            historyJobs = parseJobsHistory(buffer);
          } else if (type === 'ar') {
            opsUpdates.ar = parseARaging(buffer);
            opsUpdates.loaded = { ...(opsUpdates.loaded || {}), ar: true };
          } else if (type === 'cashflow') {
            opsUpdates.cashflow = parseCashflow(buffer);
            opsUpdates.loaded = { ...(opsUpdates.loaded || {}), cashflow: true };
          }
        } catch (e) {
          console.error(`Failed to parse ${type}:`, e);
        }
        resolve();
      });
    });

    Promise.all(promises).then(() => {
      if (Object.keys(opsUpdates).length > 0) {
        setData(prev => {
          const next = {
            ...prev,
            [entityId]: {
              ...prev[entityId],
              ...opsUpdates,
              loaded: { ...(prev[entityId]?.loaded || {}), ...opsUpdates.loaded },
            },
          };
          setSaveStatus('saving');
          saveOpsData(entityId, next[entityId])
            .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
            .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 4000); });
          return next;
        });
      }

      if (historyJobs) {
        setJobsHistory(prev => {
          const next = { ...prev, [entityId]: historyJobs };
          setSaveStatus('saving');
          saveJobsHistory(entityId, historyJobs)
            .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 2000); })
            .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 4000); });
          return next;
        });
      }
    });
  }, []);

  const updateClientMaster = useCallback((clientName, updates) => {
    setClientMaster(prev => {
      const updated = updateClient(prev, clientName, updates);
      saveClientMasterDB(updated).catch(e => console.error('Failed to save client master:', e));
      return updated;
    });
  }, []);

  if (!authed) return <Login onAuth={() => { setAccessDenied(false); setAuthed(true); }} accessDenied={accessDenied} />;

  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0a1628', flexDirection: 'column', gap: 16
      }}>
        <div style={{ fontSize: 32 }}>⏳</div>
        <div style={{ color: '#0e7c7b', fontSize: 18, fontWeight: 600 }}>Loading dashboard…</div>
        <div style={{ color: '#6b7280', fontSize: 13 }}>Fetching latest data</div>
      </div>
    );
  }

  const hasAnyData = (e) => {
    const d = data[e] || {};
    return d.jobs?.length > 0 || d.ar?.length > 0 || d.cashflow;
  };

  const getEntityData = (eid) => {
    if (eid === 'group') {
      const uae = data.uae || emptyEntityData();
      const sa = data.saudi || emptyEntityData();
      return {
        jobs: [...(uae.jobs || []), ...(sa.jobs || [])],
        ar: [...(uae.ar || []), ...(sa.ar || [])],
        cashflow: uae.cashflow || sa.cashflow,
        loaded: {
          jobs: uae.loaded.jobs || sa.loaded.jobs,
          ar: uae.loaded.ar || sa.loaded.ar,
          cashflow: uae.loaded.cashflow || sa.loaded.cashflow,
        },
      };
    }
    return data[eid] || emptyEntityData();
  };

  const currentData = getEntityData(entity);
  const currentHistoryJobs = entity === 'group'
    ? [...(jobsHistory.uae || []), ...(jobsHistory.saudi || [])]
    : (jobsHistory[entity] || []);

  const clientMetrics = computeClientMetrics(currentData.jobs || [], currentData.ar || [], clientMaster, currentHistoryJobs);
  const weeklyStats = computeWeeklyJobStats(currentData.jobs || []);
  const globalFlags = computeGlobalFlags(currentData.jobs || [], currentData.ar || [], currentData.cashflow);
  const unifiedClients = buildUnifiedClients(currentData.jobs || [], currentHistoryJobs, currentData.ar || []);
  const unifiedFlags = computeUnifiedFlags(unifiedClients);

  const unifiedClientNames = new Set(
    unifiedFlags
      .filter(f => f.client)
      .map(f => f.client.toLowerCase().replace(/\s+/g, ' ').trim())
  );

  const filteredClientMetrics = clientMetrics.map(cm => ({
    ...cm,
    flags: (cm.flags || []).filter(f => {
      if (f.type !== 'gone_quiet') return true;
      const norm = (cm.name || '').toLowerCase().replace(/\s+/g, ' ').trim();
      return !unifiedClientNames.has(norm);
    }),
  }));

  const dedupedGlobalFlags = globalFlags;
  const historyWeeklyStats = computeWeeklyVolumeStats(currentHistoryJobs);
  const clientSparklines = computeClientVolumeSparklines(currentHistoryJobs);
  const historyAlerts = computeHistoryAlerts(currentHistoryJobs);
  const ghostJobs = computeGhostJobs(currentHistoryJobs);
  const revenueGap = computeRevenueGap(currentHistoryJobs);

  const currentGlobalRedFlags = dedupedGlobalFlags.filter(f => f.severity === 'red').length;
  const currentUnifiedRedFlags = unifiedFlags.filter(f => f.severity === 'red').length;
  const currentClientRedFlags = filteredClientMetrics.flatMap(c => c.flags).filter(f => f.severity === 'red').length;
  const totalRedFlags = currentGlobalRedFlags + currentUnifiedRedFlags + currentClientRedFlags;

  const isDataEmpty = !currentData.loaded?.jobs && !currentData.loaded?.ar && !currentData.loaded?.cashflow;

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-brand">
          <span className="brand-name">TREJHARA</span>
          <span className="brand-sep">|</span>
          <span className="brand-sub">Operations</span>
        </div>
        <nav className="entity-nav">
          {ENTITIES.map(e => (
            <button
              key={e.id}
              className={`entity-btn ${entity === e.id ? 'active' : ''} ${e.id !== 'group' && !hasAnyData(e.id) ? 'no-data' : ''}`}
              onClick={() => setEntity(e.id)}
            >
              {e.label}
              {e.id !== 'group' && !hasAnyData(e.id) && <span className="no-data-dot" />}
            </button>
          ))}
        </nav>
        <div className="topbar-actions">
          {saveStatus === 'saving' && <span style={{ color: '#6b7280', fontSize: 12 }}>Saving…</span>}
          {saveStatus === 'saved' && <span style={{ color: '#10b981', fontSize: 12 }}>✓ Saved</span>}
          {saveStatus === 'error' && <span style={{ color: '#ef4444', fontSize: 12 }}>⚠ Save failed</span>}
          {totalRedFlags > 0 && (
            <button className="flag-badge" onClick={() => setTab('flags')}>
              🔴 {totalRedFlags} flags
            </button>
          )}
          <button className="btn-secondary" onClick={() => setShowSetup(true)}>Clients</button>
          <button className="btn-logout" onClick={handleLogout} title="Sign out">⏻</button>
          <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload Data</button>
        </div>
      </header>

      <nav className="tab-nav">
        {TABS.map(t => {
          const flagCount = t.id === 'flags' ? totalRedFlags : 0;
          return (
            <button
              key={t.id}
              className={`tab-btn ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {flagCount > 0 && <span className="tab-badge">{flagCount}</span>}
            </button>
          );
        })}
      </nav>

      <div className="entity-strip">
        <span className="entity-label">
          {entity === 'uae' ? '🇦🇪 UAE' : entity === 'saudi' ? '🇸🇦 Saudi Arabia' : '🌐 Group — Consolidated'}
        </span>
        {currentData.loaded?.jobs && <span className="data-tag">Jobs: {currentData.jobs.length}</span>}
        {currentData.loaded?.ar && <span className="data-tag">AR: {currentData.ar.length} clients</span>}
        {currentHistoryJobs.length > 0 && <span className="data-tag" style={{ background: '#ede9fe', color: '#7c3aed' }}>History: {currentHistoryJobs.length} jobs</span>}
        {!currentData.loaded?.jobs && !currentData.loaded?.ar && tab !== 'monthly' && tab !== 'opshistory' && (
          <span className="data-tag warn">No data — upload files to begin</span>
        )}
      </div>

      <main className="main-content">
        {tab === 'monthly' ? (
          <MonthlyMIS entity={entity} />
        ) : tab === 'opshistory' ? (
          <SaudiOps
            historyJobs={currentHistoryJobs}
            weeklyStats={historyWeeklyStats}
            alerts={historyAlerts}
            ghostJobs={ghostJobs}
            revenueGap={revenueGap}
            entity={entity}
          />
        ) : isDataEmpty ? (
          <EmptyState entity={entity} onUpload={() => setShowUpload(true)} />
        ) : (
          <>
            {tab === 'flags' && (
              <FlagsView globalFlags={[...dedupedGlobalFlags, ...unifiedFlags]} clientMetrics={filteredClientMetrics} entity={entity} allEntitiesFlags={[]} />
            )}
            {tab === 'pulse' && (
              <ClientPulse
                clients={clientMetrics}
                weeklyStats={weeklyStats}
                sparklines={clientSparklines}
                entity={entity}
                onUpdateClient={updateClientMaster}
              />
            )}
            {tab === 'pipeline' && (
              <JobsPipeline jobs={currentData.jobs} weeklyStats={weeklyStats} entity={entity} />
            )}
            {tab === 'collections' && (
              <Collections ar={currentData.ar} jobs={currentData.jobs} clientMaster={clientMaster} entity={entity} />
            )}
            {tab === 'cash' && (
              <CashView cashflow={currentData.cashflow} entity={entity} />
            )}
          </>
        )}
      </main>

      {showUpload && (
        <FileUpload
          entity={entity === 'group' ? 'uae' : entity}
          onFiles={handleFiles}
          onClose={() => setShowUpload(false)}
        />
      )}
      {showSetup && (
        <ClientSetup
          clients={clientMetrics}
          clientMaster={clientMaster}
          onUpdate={updateClientMaster}
          onClose={() => setShowSetup(false)}
        />
      )}
    </div>
  );
}

function EmptyState({ entity, onUpload }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">📁</div>
      <h2>No data loaded for {entity === 'uae' ? 'UAE' : entity === 'saudi' ? 'Saudi' : 'Group'}</h2>
      <p>Upload your SCM Profit and Tally exports to get started.</p>
      <p className="empty-files">
        Expected files: <code>OPEN_JOBS.xlsx</code> · <code>JOB_HISTORY.xlsx</code> · <code>AR_AGING.xlsx</code> · <code>CASHFLOW.xlsx</code>
      </p>
      <button className="btn-primary large" onClick={onUpload}>Upload Files</button>
    </div>
  );
}
